import React, { useEffect, useState } from "react";
import { api, ApiError } from "../../api";
import { useLanguage } from "../../i18n";

interface AnalyticsData {
  totalViews: number;
  totalVisitRequests: number;
  totalUnlocks: number;
  monthly: { month: string; views: number }[];
}

function monthLabel(m: string) {
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-IN", { month: "short" });
}

export function PropertyAnalytics({
  propertyId,
  propertyLabel,
  onBack,
  onNavigate,
}: {
  propertyId: string;
  propertyLabel: string;
  onBack: () => void;
  onNavigate?: (view: "plans" | "refer") => void;
}) {
  const { t } = useLanguage();
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [error, setError] = useState<{ message: string; status: number } | null>(null);

  useEffect(() => {
    api
      .get(`/api/properties/${propertyId}/analytics`)
      .then(setData)
      .catch((err) =>
        setError(err instanceof ApiError ? { message: err.message, status: err.status } : { message: t("common.somethingWrong"), status: 0 })
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const maxViews = Math.max(1, ...(data?.monthly.map((m) => m.views) ?? [0]));

  return (
    <>
      <button className="link-btn" style={{ marginBottom: 14 }} onClick={onBack}>
        {t("propertyAnalytics.back")}
      </button>
      <div className="reg-title">{t("propertyAnalytics.title")}</div>
      <div className="reg-sub">{propertyLabel}</div>

      {error?.status === 402 ? (
        <div className="card" style={{ marginTop: 20, textAlign: "center", padding: "28px 22px" }}>
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              background: "var(--brand-100)",
              color: "var(--brand-700)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 22,
              margin: "0 auto 16px",
            }}
          >
            📊
          </div>
          <div style={{ fontWeight: 800, fontSize: 15.5, marginBottom: 6 }}>{t("propertyAnalytics.lockedTitle")}</div>
          <p className="sub" style={{ lineHeight: 1.6, margin: "0 0 20px" }}>
            {t("propertyAnalytics.lockedBody")}
          </p>
          <button className="btn-solid" style={{ width: "100%" }} onClick={() => onNavigate?.("plans")}>
            {t("propertyAnalytics.upgradeBtn")}
          </button>
        </div>
      ) : (
        error && (
          <div className="card" style={{ marginTop: 14 }}>
            {error.message}
          </div>
        )
      )}
      {!error && !data && <div className="spinner" />}

      {data && (
        <>
          <div style={{ display: "flex", gap: 22, margin: "20px 0" }}>
            <div>
              <div className="reg-eyebrow" style={{ marginBottom: 5 }}>{t("propertyAnalytics.views")}</div>
              <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{data.totalViews}</div>
            </div>
            <div>
              <div className="reg-eyebrow" style={{ marginBottom: 5 }}>{t("propertyAnalytics.enquiries")}</div>
              <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{data.totalVisitRequests}</div>
            </div>
            <div>
              <div className="reg-eyebrow" style={{ marginBottom: 5 }}>{t("propertyAnalytics.unlocks")}</div>
              <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{data.totalUnlocks}</div>
            </div>
          </div>

          <div className="section-title" style={{ margin: "0 0 10px", fontSize: 12 }}>
            {t("propertyAnalytics.viewsByMonth")}
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 5, height: 110, marginBottom: 6 }}>
            {data.monthly.map((m) => (
              <div key={m.month} style={{ flex: 1, height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: 6 }}>
                <div
                  style={{
                    width: "100%",
                    borderRadius: 4,
                    background: "var(--brand-700)",
                    height: `${Math.max(4, (m.views / maxViews) * 100)}%`,
                  }}
                />
                <div style={{ fontSize: 10.5, textAlign: "center", color: "var(--ink-faint)" }}>{monthLabel(m.month)}</div>
              </div>
            ))}
          </div>
          {data.totalViews === 0 && <div className="field-hint" style={{ marginTop: 10 }}>{t("propertyAnalytics.emptyHint")}</div>}
        </>
      )}
    </>
  );
}
